/**
 * The editor's top bar.
 *
 * Three groups, left to right: what this is (brand, plan name, whether it is
 * saved), what you just did (undo/redo), and what you can do next. There is
 * exactly one filled button in it — the export — because the printed plan is
 * what all the rest is for; everything else is chrome and is drawn as chrome.
 * The backup sits beside it as chrome: with no server, it is the planner's only
 * copy of their work outside this browser, so it is never more than a click away.
 *
 * The shortcut list used to live at the bottom of the inspector, where it took
 * a third of a panel that has actual work to do. It is a reference, consulted
 * twice and then never again, so it belongs behind a `?`.
 *
 * On a phone the bar keeps what is needed every minute — the way back, the
 * name, whether it saved, undo and redo — and folds the rest behind `⋯`. The
 * PDF is still one tap further away in the Plan panel, next to the checklist
 * that says whether it is ready.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { type ReactNode, useEffect, useRef, useState } from 'react'

import { TOUCH_FIRST, useMediaQuery } from '../hooks/useMediaQuery'
import { api } from '../lib/api'
import { FEATURES } from '../lib/config'
import type { LocaleCode } from '../lib/i18n'
import { useT } from '../lib/i18n/I18nProvider'
import type { Catalogue } from '../lib/i18n'
import type { Plan } from '../lib/types'
import { useDraftText } from '../hooks/useDraftText'
import { type SaveStatus, usePlanStore } from '../store/planStore'
import { LanguagePicker } from './LanguagePicker'
import { ThemeSwitch } from './ThemeSwitch'

interface Props {
  plan: Plan
  onExport: () => void
  onOpenSettings: () => void
  onBackup: () => void
  backingUp: boolean
  /** Changed enough since the last backup that the button says so. */
  backupDue: boolean
  /** The phone layout: brand mark only, save status as a dot, everything else in a menu. */
  compact?: boolean
  /** Panel buttons carried in the bar itself, on a phone held sideways. */
  panels?: ReactNode
}

export function TopBar({
  plan,
  onExport,
  onOpenSettings,
  onBackup,
  backingUp,
  backupDue,
  compact = false,
  panels,
}: Props) {
  const t = useT()
  const undo = usePlanStore((s) => s.undo)
  const redo = usePlanStore((s) => s.redo)
  const canUndo = usePlanStore((s) => s.past.length > 0)
  const canRedo = usePlanStore((s) => s.future.length > 0)
  const saveStatus = usePlanStore((s) => s.saveStatus)
  const saveError = usePlanStore((s) => s.saveError)

  const [helpOpen, setHelpOpen] = useState(false)

  const history = (
    <div className="topbar-history">
      <button
        type="button"
        className="btn btn-chrome btn-icon"
        onClick={undo}
        disabled={!canUndo}
        title={t.topbar.undoHint}
        aria-label={t.topbar.undo}
      >
        <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" className="icon--directional">
          <path
            d="M9 14L4 9l5-5M4 9h9a7 7 0 0 1 0 14h-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <button
        type="button"
        className="btn btn-chrome btn-icon"
        onClick={redo}
        disabled={!canRedo}
        title={t.topbar.redoHint}
        aria-label={t.topbar.redo}
      >
        <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" className="icon--directional">
          <path
            d="M15 14l5-5-5-5M20 9h-9a7 7 0 0 0 0 14h3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  )

  if (compact) {
    return (
      <header className="topbar topbar--compact">
        <div className="topbar-left">
          <Link to="/plans" className="brand" title={t.common.allPlans} aria-label={t.common.allPlans}>
            <BrandMark />
          </Link>
          <PlanTitle plan={plan} />
          <SaveBadge status={saveStatus} error={saveError} copy={t.topbar.save} compact />
        </div>

        <div className="topbar-right">
          {panels}
          {history}
          <EditorMenu
            plan={plan}
            onExport={onExport}
            onOpenSettings={onOpenSettings}
            onBackup={onBackup}
            backingUp={backingUp}
            backupDue={backupDue}
          />
        </div>
      </header>
    )
  }

  return (
    <header className="topbar">
      <div className="topbar-left">
        <Link to="/plans" className="brand" title={t.common.allPlans}>
          <BrandMark />
          <span className="brand-name">Yerbəyer</span>
        </Link>

        <span className="topbar-divider" />

        <PlanTitle plan={plan} />
        <SaveBadge status={saveStatus} error={saveError} copy={t.topbar.save} />

        <span className="topbar-divider" />

        {history}
      </div>

      <div className="topbar-right">
        <ThemeSwitch />
        <LanguagePicker variant="plan" onChange={(next) => void setPlanLanguage(plan, next)} />

        <HelpPopover open={helpOpen} onToggle={() => setHelpOpen((v) => !v)} onClose={() => setHelpOpen(false)} />

        <button type="button" className="btn btn-chrome topbar-action" onClick={onOpenSettings}>
          {t.topbar.tableSettings}
        </button>

        <button
          type="button"
          className={`btn btn-chrome topbar-action${backupDue ? ' is-due' : ''}`}
          onClick={onBackup}
          disabled={backingUp}
          title={backupDue ? t.editor.backupReminder : t.topbar.backupHint}
        >
          {t.topbar.backup}
        </button>

        {FEATURES.pdfExport && (
          <button type="button" className="btn btn-primary topbar-export" onClick={onExport}>
            {t.topbar.downloadPdf}
          </button>
        )}
      </div>
    </header>
  )
}

/**
 * The plan name reads as a heading until you decide to change it — a permanent
 * text box in the top bar makes the title look like a form field on a page that
 * is not a form. The pencil is the affordance; clicking the name works too.
 *
 * While the field is open the text lives **here**, not in the document. Writing
 * every keystroke straight to the plan meant the title could never hold a
 * character the plan would not accept: a trailing space vanished as it was
 * typed, so a two-word name was unreachable, and clearing the field to start
 * over stamped a placeholder on before the first new letter arrived. It also
 * spent an undo entry and an autosave per character, so taking a rename back
 * meant pressing ⌘Z once per letter. A local buffer fixes all three: the plan
 * hears one rename, when the planner is finished.
 */
function PlanTitle({ plan }: { plan: Plan }) {
  const t = useT()
  const renamePlan = usePlanStore((s) => s.renamePlan)
  const [editing, setEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Blank is a field mid-retype, not a rename: the store leaves the old title
  // alone, so walking away from an empty box loses nothing.
  const title = useDraftText(plan.title, renamePlan, () => setEditing(false))

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const open = () => {
    title.begin(plan.title)
    setEditing(true)
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="plan-title-input"
        {...title.bind}
        aria-label={t.topbar.planTitle}
        maxLength={160}
      />
    )
  }

  return (
    <span className="plan-title">
      <button type="button" className="plan-title-text" onClick={open}>
        {plan.title}
      </button>
      <button
        type="button"
        className="plan-title-edit"
        onClick={open}
        title={t.topbar.rename}
        aria-label={t.topbar.rename}
      >
        <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
          <path
            d="M4 20h4L19 9l-4-4L4 16v4zM14.5 5.5l4 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </span>
  )
}

function HelpPopover({
  open,
  onToggle,
  onClose,
}: {
  open: boolean
  onToggle: () => void
  onClose: () => void
}) {
  const t = useT()
  const wrapRef = useRef<HTMLDivElement>(null)

  // Click-away and Escape. Both matter: this opens over a canvas people are
  // about to click on, and having to find the `?` again to close it is a tax.
  useEffect(() => {
    if (!open) return undefined
    const onDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) onClose()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('pointerdown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  const touch = useMediaQuery(TOUCH_FIRST)

  return (
    <div className="help-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`btn btn-chrome btn-icon${open ? ' is-on' : ''}`}
        onClick={onToggle}
        title={t.topbar.help}
        aria-label={t.topbar.help}
        aria-expanded={open}
      >
        ?
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="help-popover"
            role="dialog"
            aria-label={t.inspector.shortcuts}
            /* Scales from its own top-right corner — the button it came out of
               — rather than from its centre, and never from scale(0). */
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -2 }}
            transition={{ duration: 0.14, ease: [0.23, 1, 0.32, 1] }}
          >
            <span className="eyebrow">{touch ? t.gestures.title : t.inspector.shortcuts}</span>
            <HelpRows touch={touch} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * What the editor answers to. With a mouse and keyboard, the shortcuts; on a
 * touch screen, the gestures — a list of key chords is no help to a thumb.
 */
function HelpRows({ touch }: { touch: boolean }) {
  const t = useT()
  const rows: Array<[string, string]> = touch
    ? [
        [t.gestures.zoom, t.gestures.pinch],
        [t.inspector.shortcut.panAround, t.gestures.dragFloor],
        [t.inspector.shortcut.selectTable, t.gestures.tap],
        [t.inspector.shortcut.moveIt, t.gestures.dragIt],
        [t.inspector.shortcut.dropRoundTable, t.gestures.doubleTap],
        [t.gestures.seatGuest, t.gestures.tapNameThenChair],
        [t.gestures.swapGuests, t.gestures.dragOntoOther],
        [t.inspector.shortcut.addToSelection, t.gestures.selectThenTap],
        [t.inspector.shortcut.boxSelect, t.gestures.selectThenDrag],
      ]
    : [
        [t.inspector.shortcut.moveIt, t.inspector.shortcut.drag],
        [t.inspector.shortcut.ignoreGrid, 'alt + drag'],
        [t.inspector.shortcut.panAround, t.inspector.shortcut.dragCanvas],
        [t.inspector.shortcut.zoomToCursor, t.inspector.shortcut.scroll],
        [t.inspector.shortcut.dropRoundTable, t.inspector.shortcut.doubleClick],
        [t.inspector.shortcut.cycleRsvp, t.inspector.shortcut.doubleClickChair],
        [t.inspector.shortcut.undo, '⌘ Z'],
        [t.inspector.shortcut.removeSelection, t.inspector.shortcut.del],
        [t.inspector.shortcut.addToSelection, t.inspector.shortcut.shiftClick],
        [t.inspector.shortcut.boxSelect, t.inspector.shortcut.shiftDrag],
        [t.inspector.shortcut.selectAll, '⌘ A'],
      ]

  return (
    <dl className={`help-rows${touch ? ' help-rows--gestures' : ''}`}>
      {rows.map(([action, how]) => (
        <div className="help-row" key={action}>
          <dt>{action}</dt>
          <dd className={touch ? undefined : 'mono'}>{how}</dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * Everything the phone's top bar has no room for, one tap away: the plan's
 * language, table defaults, the backup (marked when one is due, as the desktop
 * button is), the PDF, and the help list.
 */
function EditorMenu({
  plan,
  onExport,
  onOpenSettings,
  onBackup,
  backingUp,
  backupDue,
}: Omit<Props, 'compact' | 'panels'>) {
  const t = useT()
  const touch = useMediaQuery(TOUCH_FIRST)
  const [open, setOpen] = useState(false)
  const [help, setHelp] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const close = () => {
    setOpen(false)
    setHelp(false)
  }

  useEffect(() => {
    if (!open) return undefined
    const onDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false)
        setHelp(false)
      }
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        setHelp(false)
      }
    }
    window.addEventListener('pointerdown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const run = (action: () => void) => () => {
    close()
    action()
  }

  return (
    <div className="help-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`btn btn-chrome btn-icon topbar-more${open ? ' is-on' : ''}${backupDue ? ' is-due' : ''}`}
        onClick={() => (open ? close() : setOpen(true))}
        aria-label={t.panels.more}
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <circle cx="5.5" cy="12" r="1.7" fill="currentColor" />
          <circle cx="12" cy="12" r="1.7" fill="currentColor" />
          <circle cx="18.5" cy="12" r="1.7" fill="currentColor" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="help-popover editor-menu"
            role="dialog"
            aria-label={help ? (touch ? t.gestures.title : t.inspector.shortcuts) : t.panels.more}
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -2 }}
            transition={{ duration: 0.14, ease: [0.23, 1, 0.32, 1] }}
          >
            {help ? (
              <>
                <button type="button" className="editor-menu-back" onClick={() => setHelp(false)}>
                  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" className="icon--directional">
                    <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="eyebrow">{touch ? t.gestures.title : t.inspector.shortcuts}</span>
                </button>
                <HelpRows touch={touch} />
              </>
            ) : (
              <>
                <div className="editor-menu-language">
                  <LanguagePicker variant="plan" onChange={(next) => void setPlanLanguage(plan, next)} />
                  <ThemeSwitch />
                </div>
                <button type="button" className="editor-menu-item" onClick={run(onOpenSettings)}>
                  {t.topbar.tableSettings}
                </button>
                <button
                  type="button"
                  className={`editor-menu-item${backupDue ? ' is-due' : ''}`}
                  onClick={run(onBackup)}
                  disabled={backingUp}
                >
                  {t.topbar.backup}
                </button>
                {FEATURES.pdfExport && (
                  <button type="button" className="editor-menu-item" onClick={run(onExport)}>
                    {t.topbar.downloadPdf}
                  </button>
                )}
                <button type="button" className="editor-menu-item" onClick={() => setHelp(true)}>
                  {touch ? t.gestures.title : t.topbar.help}
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * Language is written on its own rather than through the layout save because it
 * is the one piece of plan metadata the merge does *not* own — the name, date
 * and venue are part of the merged document and ride the versioned save with
 * it, but language is chrome-and-print, held by the plan record alone.
 * Written immediately rather than debounced: it is one deliberate click.
 */
async function setPlanLanguage(plan: Plan, language: LocaleCode): Promise<void> {
  try {
    await api.updatePlan(plan.id, { language })
  } catch {
    /* non-fatal: the editor has already switched, and a reload re-reads it */
  }
}

function SaveBadge({
  status,
  error,
  copy,
  compact = false,
}: {
  status: SaveStatus
  error: string | null
  copy: Catalogue['topbar']['save']
  /** Just the dot; the words stay for screen readers and a long press. */
  compact?: boolean
}) {
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={status}
        className={`save-badge save-badge--${status}${compact ? ' save-badge--dot' : ''}`}
        initial={{ opacity: 0, y: -3 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 3 }}
        transition={{ duration: 0.14 }}
        title={error ?? (compact ? copy[status] : undefined)}
        role={compact ? 'status' : undefined}
      >
        <span className="dot save-dot" />
        {compact ? <span className="sr-only">{copy[status]}</span> : copy[status]}
      </motion.span>
    </AnimatePresence>
  )
}

/**
 * The mark: a round table seen from above with four chairs around it, in the
 * three table colours. It is the product drawn at 24px — the thing every plan
 * is made of — so it stays literal rather than becoming an abstract chip.
 */
export function BrandMark() {
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true" className="brand-mark">
      <circle cx="16" cy="16" r="8.5" fill="#E4EEF8" stroke="#7FA6D0" strokeWidth="1.8" />
      <circle cx="16" cy="4.5" r="3" fill="#4C7FB5" />
      <circle cx="27.5" cy="16" r="3" fill="#A9628B" />
      <circle cx="16" cy="27.5" r="3" fill="#B4714F" />
      <circle cx="4.5" cy="16" r="3" fill="#4C7FB5" />
    </svg>
  )
}
